// PresenceHeartbeat — while the user is logged in AND has opted in to
// "nudge me to review places I visit", periodically POST their coarse
// location to /api/me/presence. Server matches against place-y groups
// within 50m and upserts a per-day visit row; an hourly cron sends a
// bell notification ("Rate your visit to Deer Park Café") 24h later.
//
// Zero UI — renders null. Silent when opt-in is off, permission denied,
// or browser lacks geolocation.
//
// Uses `enableHighAccuracy: false` for a coarse fix (city-block level
// suffices for a 50m match around a shop); low battery cost.
//
// Interval is 5 min while the tab is visible, 20 min when hidden.

import { useEffect } from 'react';
import { useApi } from '../lib/api';
import { useAuth } from '../lib/auth';

const HEARTBEAT_MS_VISIBLE = 5 * 60 * 1000;
const HEARTBEAT_MS_HIDDEN  = 20 * 60 * 1000;

export function PresenceHeartbeat() {
    const api = useApi();
    const { authed } = useAuth();

    useEffect(() => {
        if (!authed) return;
        if (typeof navigator === 'undefined' || !navigator.geolocation) return;

        let stopped = false;
        let timer: number | null = null;
        let optedIn = false;

        async function checkOptIn() {
            try {
                const me = await api.get<{ review_visits_opt_in?: boolean }>('/api/me');
                optedIn = !!me.review_visits_opt_in;
            } catch { optedIn = false; }
        }

        function fire() {
            if (stopped || !optedIn) return schedule();
            navigator.geolocation.getCurrentPosition(
                async (pos) => {
                    try {
                        await api.post('/api/me/presence', {
                            lat: pos.coords.latitude,
                            lon: pos.coords.longitude,
                        });
                    } catch { /* swallow — best-effort */ }
                    schedule();
                },
                () => schedule(),   // denied / timed out — try again next tick
                { enableHighAccuracy: false, maximumAge: 3 * 60 * 1000, timeout: 15_000 },
            );
        }

        function schedule() {
            if (stopped) return;
            const ms = document.hidden ? HEARTBEAT_MS_HIDDEN : HEARTBEAT_MS_VISIBLE;
            timer = window.setTimeout(fire, ms);
        }

        // Kick off: check opt-in, fire once, then schedule.
        void (async () => {
            await checkOptIn();
            fire();
        })();

        // Re-check opt-in whenever the tab becomes visible again.
        function onVis() {
            if (!document.hidden) void checkOptIn();
        }
        document.addEventListener('visibilitychange', onVis);

        return () => {
            stopped = true;
            if (timer) window.clearTimeout(timer);
            document.removeEventListener('visibilitychange', onVis);
        };
    }, [api, authed]);

    return null;
}
